import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import Icon from 'components/AppIcon';
import { useAuth } from 'context/AuthContext';
import { useEmployerApplications } from 'hooks/useEmployerApplications';

const summaryCards = (applications) => {
  const totals = applications.reduce(
    (acc, app) => {
      const shortlisted = app.localStats?.shortlist?.active ?? 0;
      const submitted = app.localStats?.workers?.selected ?? 0;
      const approved = app.localStats?.workers?.approved ?? 0;
      const remaining = app.slots?.available ?? 0;
      return {
        applications: acc.applications + 1,
        shortlist: acc.shortlist + shortlisted,
        submitted: acc.submitted + submitted,
        approved: acc.approved + approved,
        slots: acc.slots + remaining
      };
    },
    { applications: 0, shortlist: 0, submitted: 0, approved: 0, slots: 0 }
  );

  return [
    {
      label: 'Active VDR Applications',
      value: totals.applications,
      icon: 'FileText',
      accent: 'text-primary-600'
    },
    {
      label: 'Workers on Hold',
      value: totals.shortlist,
      icon: 'Clock',
      accent: 'text-amber-600'
    },
    {
      label: 'Submitted Workers',
      value: totals.submitted,
      icon: 'Send',
      accent: 'text-blue-600'
    },
    {
      label: 'Selected Workers',
      value: totals.approved,
      icon: 'CheckCircle',
      accent: 'text-green-600'
    },
    {
      label: 'Slots Available',
      value: totals.slots,
      icon: 'Users',
      accent: 'text-purple-600'
    }
  ];
};

const EmployerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const { applications, categories, loading, error } = useEmployerApplications();

  const cards = useMemo(() => summaryCards(applications), [applications]);

  const topCategories = categories.slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar
        userRole="employer"
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        userName={user?.fullName || 'Employer'}
      />

      <RoleBasedSidebar
        userRole="employer"
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main className={`transition-all duration-300 ease-smooth pt-16 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6 space-y-6">
          <BreadcrumbNavigation
            items={[{ label: 'Dashboard', path: '/employer-dashboard' }]}
          />

          <section className="bg-white border border-secondary-200 rounded-lg shadow-sm p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-text-primary">Employer Dashboard</h1>
                <p className="text-text-secondary mt-1">
                  Quick snapshot of your E-CLAB applications, workers, and available slots
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => navigate('/employer-application-management')}
                  className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700"
                >
                  <Icon name="ClipboardList" size={16} className="mr-2" />
                  Manage Applications
                </button>
                <button
                  onClick={() => navigate('/employer-biodata-selection')}
                  className="inline-flex items-center px-4 py-2 bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200"
                >
                  <Icon name="UserCheck" size={16} className="mr-2" />
                  Select Workers
                </button>
              </div>
            </div>
          </section>

          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4 text-error">
              {error}
            </div>
          )}

          <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {cards.map((card) => (
              <div key={card.label} className="bg-white border border-secondary-200 rounded-lg p-5 shadow-sm">
                <div className={`w-12 h-12 rounded-full bg-secondary-50 flex items-center justify-center mb-4 ${card.accent}`}>
                  <Icon name={card.icon} size={20} />
                </div>
                <div className="text-xs text-text-secondary uppercase tracking-wide mb-1">
                  {card.label}
                </div>
                <div className="text-3xl font-semibold text-text-primary">{card.value}</div>
              </div>
            ))}
          </section>

          <section className="bg-white border border-secondary-200 rounded-lg p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Top VDR Categories</h2>
            {loading ? (
              <div className="text-text-secondary text-sm">Loading categories…</div>
            ) : topCategories.length === 0 ? (
              <div className="text-text-secondary text-sm">No VDR applications found yet.</div>
            ) : (
              <div className="space-y-3">
                {topCategories.map((category) => (
                  <div key={category.key} className="flex items-center justify-between bg-secondary-50 border border-secondary-200 rounded-lg px-4 py-3">
                    <div>
                      <div className="text-sm font-semibold text-text-primary">{category.label}</div>
                      <div className="text-xs text-text-secondary">{category.selectable} ready for worker selection</div>
                    </div>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-50 text-primary-700">
                      {category.total} applications
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default EmployerDashboard;
